/**
 * CR454: Test Image Capture Modal
 * Shown when admin clicks "Test Image Capture" on a FLYER source with capture enabled.
 */

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Camera, CheckCircle2, ExternalLink, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { MerchantSource } from "@/hooks/useMerchantSources";

interface TestImageCaptureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: MerchantSource | null;
}

interface TestRunResult {
  test_run_id: string;
  status: string;
  adapter_key: string;
  adapter_display_name: string;
  entrypoint: string;
  merchant_id: string;
  postal_code: string | null;
  max_pages: number;
  ui_url: string;
  message: string;
}

export function TestImageCaptureModal({
  open,
  onOpenChange,
  source,
}: TestImageCaptureModalProps) {
  const [postalCode, setPostalCode] = useState<string>("");
  const [maxPages, setMaxPages] = useState<number>(5);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TestRunResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setResult(null);
      setError(null);
      setLoading(false);
    }
    onOpenChange(open);
  };

  // Seed postal code from source default when modal opens
  const handleOpen = () => {
    const defaultPostal = (source as any)?.default_postal_code ?? "";
    setPostalCode(defaultPostal);
    setMaxPages(5);
    setResult(null);
    setError(null);
  };

  const handleRun = async () => {
    if (!source) return;
    setLoading(true);
    setError(null);

    try {
      const { data, error: fnErr } = await supabase.functions.invoke(
        "flyer-image-capture-test",
        {
          body: {
            merchant_source_id: source.id,
            max_pages: maxPages,
            postal_code_override: postalCode.trim() || null,
          },
        }
      );

      if (fnErr) {
        throw new Error(fnErr.message || "Edge function error");
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      setResult(data as TestRunResult);
      toast.success(`Test run queued (ID: ${data.test_run_id.slice(0, 8)}…)`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      setError(msg);
      toast.error("Failed to create test run");
    } finally {
      setLoading(false);
    }
  };

  const navigateToResults = () => {
    if (!result?.test_run_id) return;
    // Navigate to Flyer Capture Console with test_run_id filter
    window.open(`/admin/flyer-capture?test_run_id=${result.test_run_id}`, "_blank");
  };

  if (!source) return null;

  const adapterKey = (source as any).flyer_image_capture_adapter_key as string | undefined;
  const requiresRegion = (source as any).requires_region as boolean | undefined;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        onOpenAutoFocus={handleOpen}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5 text-primary" />
            Test Image Capture
          </DialogTitle>
          <DialogDescription>
            Run a quick capture test for{" "}
            <strong>{source.merchant_name || source.merchant_id}</strong>.
            Results are queued for the Python scheduler.
          </DialogDescription>
        </DialogHeader>

        {/* Source details */}
        <div className="space-y-3">
          <div className="rounded-md border bg-muted/30 p-3 space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Adapter</span>
              <Badge variant="outline" className="font-mono text-xs">
                {adapterKey || "—"}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Source URL</span>
              <a
                href={source.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline flex items-center gap-1 max-w-[200px] truncate"
              >
                {source.source_url}
                <ExternalLink className="h-3 w-3 flex-shrink-0" />
              </a>
            </div>
            {requiresRegion && (
              <div className="flex items-center gap-1.5">
                <Badge variant="secondary" className="text-xs">Region-aware</Badge>
                <span className="text-xs text-muted-foreground">
                  Postal code will be used for region resolution
                </span>
              </div>
            )}
          </div>

          {/* Config fields */}
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="postal-code">
                Postal Code
                {requiresRegion && (
                  <span className="text-destructive ml-1">*</span>
                )}
              </Label>
              <Input
                id="postal-code"
                placeholder="e.g. M5V 2T6"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                {requiresRegion
                  ? "Required for region resolution"
                  : "Optional — overrides source default"}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="max-pages">Max Pages (1–20)</Label>
              <Input
                id="max-pages"
                type="number"
                min={1}
                max={20}
                value={maxPages}
                onChange={(e) =>
                  setMaxPages(Math.min(20, Math.max(1, parseInt(e.target.value) || 5)))
                }
              />
              <p className="text-xs text-muted-foreground">
                Limits captured pages to keep test runs fast
              </p>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {/* Success */}
          {result && (
            <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/10 p-3 text-sm">
              <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0 text-primary" />
              <div className="space-y-1">
                <p className="font-medium text-foreground">Test run queued!</p>
                <p className="text-muted-foreground text-xs">
                  Run ID:{" "}
                  <code className="font-mono">{result.test_run_id}</code>
                </p>
                <p className="text-muted-foreground text-xs">
                  {result.message}
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={loading}
          >
            {result ? "Close" : "Cancel"}
          </Button>
          {result ? (
            <Button onClick={navigateToResults} className="gap-2">
              <ExternalLink className="h-4 w-4" />
              View in Capture Console
            </Button>
          ) : (
            <Button onClick={handleRun} disabled={loading} className="gap-2">
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Queuing…
                </>
              ) : (
                <>
                  <Camera className="h-4 w-4" />
                  Run Test
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
